// InShellXPBadge — Phase 6 dopamine.
//
// A tiny floating "+N points → {context}" pill that appears for ~3s
// inside the chat shell whenever the signed-in user's `user_xp.total_xp`
// row increases. Lives next to the global `useXPGainToasts` toast but is
// scoped to the shell so users feel the action *here* (not just "a toast
// fired somewhere"). Context line uses the active playbook title (or the
// active mode) so the badge feels tied to *what they just did*.
import { useEffect, useRef, useState } from "react";
import { Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface Props {
  /** Human-readable label appended after the +N points (e.g. playbook title). */
  context?: string;
}

const InShellXPBadge = ({ context }: Props) => {
  const { user } = useAuth();
  const [delta, setDelta] = useState<number | null>(null);
  const lastXpRef = useRef<number | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!user) return;

    // Seed baseline so the first realtime tick isn't treated as a +N gain.
    (async () => {
      const { data } = await supabase
        .from("user_xp")
        .select("total_xp")
        .eq("user_id", user.id)
        .maybeSingle();
      lastXpRef.current = data?.total_xp ?? 0;
    })();

    const channelName = `xp_badge_shell:${user.id}:${Math.random().toString(36).slice(2, 8)}`;
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "user_xp",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new as { total_xp?: number } | null;
          if (!row?.total_xp == null) return;
          const next = row.total_xp ?? 0;
          const prev = lastXpRef.current;
          if (prev !== null && next > prev) {
            setDelta(next - prev);
            if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
            hideTimerRef.current = setTimeout(() => setDelta(null), 3200);
          }
          lastXpRef.current = next;
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [user?.id]);

  if (!delta) return null;

  return (
    <div
      className="pointer-events-none absolute top-3 right-3 z-20 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-primary text-primary-foreground text-[11px] font-mono font-bold shadow-lg border border-primary/40 animate-in fade-in slide-in-from-top-2 duration-300"
      role="status"
      aria-live="polite"
    >
      <Sparkles className="w-3.5 h-3.5" />
      +{delta.toLocaleString()} points
      {context && (
        <span className="font-normal opacity-90 ml-1 max-w-[140px] truncate">
          → {context}
        </span>
      )}
    </div>
  );
};

export default InShellXPBadge;